import { useState, useEffect, useCallback } from 'react';
import config from '../config';

// Standalone mode detection
// In Electron, backend is embedded - ALWAYS use it (not localStorage)
const isStandaloneClient = () => {
  // If running in Electron, use the embedded backend database
  if (window.electronAPI) {
    return false; // Use backend at http://localhost:5001
  }
  // Only use localStorage for web browsers without backend
  return !window.location.href.includes('localhost');
};

// Local storage utilities for standalone mode
const LOCAL_STORAGE_KEYS = {
  clients: 'travelit_clients',
  packages: 'travelit_packages',  
  reservations: 'travelit_reservations',
  suppliers: 'travelit_suppliers',
  financial: 'travelit_financial',
  documents: 'travelit_documents'
};

// Get data from local storage
const getLocalData = (key) => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEYS[key]);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error(`Error reading local data for ${key}:`, error);
    return [];
  }
};

// Save data to local storage
const setLocalData = (key, data) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEYS[key], JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving local data for ${key}:`, error);
  }
};

// Generate unique ID for local data
const generateLocalId = () => {
  return Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Data cache for performance - memory only, no localStorage
const dataCache = {};
const CACHE_DURATION = 30 * 1000; // 30 secondes - cache ultra-court pour performances maximales

// Simple in-flight/throttle guard to avoid flooding the API (especially during hot reload)
const fetchLocks = {
  reservations: { inFlight: false, last: 0 },
  clients: { inFlight: false, last: 0 },
  packages: { inFlight: false, last: 0 },
  financial: { inFlight: false, last: 0 },
  documents: { inFlight: false, last: 0 },
  suppliers: { inFlight: false, last: 0 },
  calendar: { inFlight: false, last: 0 }
};

const shouldSkipFetch = (key, minIntervalMs = 100) => {
  const now = Date.now();
  const lock = fetchLocks[key];
  if (!lock) return false;
  if (lock.inFlight) return true;
  if (now - lock.last < minIntervalMs) return true;
  fetchLocks[key].inFlight = true;
  return false;
};

const completeFetch = (key) => {
  if (fetchLocks[key]) {
    fetchLocks[key].inFlight = false;
    fetchLocks[key].last = Date.now();
  }
};

// Global data versioning for real-time sync
let globalDataVersion = {
  reservations: 1,
  clients: 1,
  packages: 1,
  financial: 1,
  calendar: 1,
  suppliers: 1,
  documents: 1
};

// Cross-section data dependencies mapping
const dataDependencies = {
  clients: ['reservations', 'financial', 'documents'],
  reservations: ['clients', 'financial', 'packages'],
  packages: ['reservations', 'financial'],
  financial: ['clients', 'reservations'],
  suppliers: ['packages', 'financial'],
  documents: ['clients', 'reservations'],
  calendar: ['reservations']
};

// Utility functions
const isCacheValid = (key) => {
  const cached = dataCache[key];
  return cached && (Date.now() - cached.timestamp) < CACHE_DURATION;
};

const invalidateCache = (key) => {
  delete dataCache[key];
  globalDataVersion[key]++;
  
  // Trigger cross-section cache invalidation for dependent data
  const dependencies = dataDependencies[key] || [];
  dependencies.forEach(depKey => {
    delete dataCache[depKey];
    globalDataVersion[depKey]++;
  });
};

// Map data types to standard keys (no localStorage used)
const getDataTypeKey = (type) => {
  const keyMap = {
    'reservation': 'reservations',
    'package': 'packages', 
    'transaction': 'financial',
    'client': 'clients',
    'supplier': 'suppliers',
    'document': 'documents',
    'reservations': 'reservations',
    'packages': 'packages',
    'clients': 'clients',
    'financial': 'financial'
  };
  return keyMap[type] || type;
};

// Enhanced notification system for real-time updates
const notifyDataChange = (dataType, action, data = null) => {
  // Standardize data types to match cache keys for consistent event naming
  const standardizedType = getDataTypeKey(dataType);
  
  // Specific data type event
  window.dispatchEvent(new CustomEvent(`${standardizedType}DataUpdate`, {
    detail: { action, data, timestamp: Date.now() }
  }));
  
  // Global data refresh event
  window.dispatchEvent(new CustomEvent('globalDataRefresh', {
    detail: { 
      source: standardizedType, 
      action, 
      timestamp: Date.now(),
      affectedData: [standardizedType, ...(dataDependencies[standardizedType] || [])]
    }
  }));
  
  // Force cache invalidation for better data consistency
  setTimeout(() => {
    invalidateCache(standardizedType);
  }, 50);
};

// API utility function with better error handling and timeout
const apiCall = async (url, options = {}) => {
  const isFormData = options.body instanceof FormData;
  const isGetLike = !options.method || options.method.toUpperCase() === 'GET';
  
  try {
    // Add timeout to prevent hanging requests
    // Longer timeout for Electron to allow backend initialization
    const controller = new AbortController();
    const timeoutDuration = window.electronAPI ? 15000 : 8000; // 15s for Electron, 8s for web
    const timeoutId = setTimeout(() => controller.abort(), timeoutDuration);
    
    const response = await fetch(url, {
      headers: {
        // Do NOT send Content-Type for GET/HEAD to avoid unnecessary CORS preflights
        ...(!isFormData && !isGetLike ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers
      },
      credentials: 'include',
      signal: controller.signal,
      ...options
    });
    
    clearTimeout(timeoutId);

    if (!response.ok) {
      // For DELETE requests on suppliers and forfaits, special handling to ensure UI updates
      if (options.method === 'DELETE' && (url.includes('/suppliers/') || url.includes('/forfaits/'))) {
        console.log(`🗑️ ${url.includes('/forfaits/') ? 'Forfait' : 'Supplier'} delete request - forcing success response despite ${response.status} error`);
        return { success: true, data: { deleted: true, id: url.split('/').pop() } };
      }
      
      // For all other errors, throw the error to be handled by the caller
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    // Handle timeout and network errors gracefully
    if (error.name === 'AbortError') {
      console.warn(`API request timeout for ${url}`);
      throw new Error(`Request timeout: ${url}`);
    }
    
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.warn(`Network error for ${url}`);
      // Special handling for supplier and forfait deletion to ensure UI updates
      if (options.method === 'DELETE' && (url.includes('/suppliers/') || url.includes('/forfaits/'))) {
        console.log(`🗑️ ${url.includes('/forfaits/') ? 'Forfait' : 'Supplier'} delete request - forcing success response despite network error`);
        return { success: true, data: { deleted: true, id: url.split('/').pop() } };
      }
      throw new Error(`Network error: Unable to connect to server`);
    }
    
    // Rethrow other errors
    throw error;
  }
};

// Reservations Data Hook
export const useReservationsData = (filters = {}) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false); // Chargement optimisé
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    confirmed: 0,
    pending: 0,
    cancelled: 0
  });

  const fetchReservationsData = useCallback(async () => {
    try {
      setError(null);

      // Affichage immédiat du cache s'il existe
      if (isCacheValid('reservations')) {
        const cached = dataCache.reservations;
        setReservations(cached.data);
        setStats(cached.stats);
        setLoading(false);
        return;
      }

      // Éviter les appels API redondants avec délai réduit
      if (shouldSkipFetch('reservations')) {
        // Affichage immédiat des données vides plutôt que d'attendre
        setLoading(false);
        return;
      }

      // Pas de setLoading(true) - on garde l'affichage fluide
      let reservationsData = [];
      let packagesData = [];

      // API only
      const reservationsResponse = await apiCall(`${config.apiUrl}/reservations`);
      const packagesResponse = await apiCall(`${config.apiUrl}/forfaits`);
      
      reservationsData = reservationsResponse.data || reservationsResponse || [];
      packagesData = packagesResponse.data || packagesResponse || [];

      // Ensure data is always an array before processing
      const safeReservationsData = Array.isArray(reservationsData) ? reservationsData : [];
      const safePackagesData = Array.isArray(packagesData) ? packagesData : [];

      // Process and filter data
      let processedReservations = safeReservationsData.map(res => ({
        ...res,
        forfait: safePackagesData.find(pkg => pkg.id === res.forfaitId)?.titre || 'N/A'
      }));

      // Apply filters
      if (filters.status) {
        processedReservations = processedReservations.filter(res => res.statut === filters.status);
      }
      if (filters.client) {
        processedReservations = processedReservations.filter(res => 
          res.clientId === filters.client || res.clientNom === filters.client
        );
      }

      // Calculate stats
      const confirmed = processedReservations.filter(r => r.statut === 'Confirmée').length;
      const pending = processedReservations.filter(r => r.statut === 'En attente').length;
      const cancelled = processedReservations.filter(r => r.statut === 'Annulée').length;
      
      const statsData = {
        total: processedReservations.length,
        confirmed,
        pending,
        cancelled
      };

      // Cache results
      dataCache.reservations = {
        data: processedReservations,
        stats: statsData,
        timestamp: Date.now()
      };

      setReservations(processedReservations);
      setStats(statsData);
      setLoading(false);

    } catch (error) {
      console.error('Error fetching reservations:', error);
      setError(error.message);
      setLoading(false);
    } finally {
      completeFetch('reservations');
    }
  }, [filters]);

    useEffect(() => {
    fetchReservationsData();

    // Listen for data updates (both direct and cross-section)
    const handleUpdate = (e) => {
      const detail = (e && e.detail) ? e.detail : {};
      const { source, action } = detail;
      console.log(`Reservations updating due to ${source || 'direct'} ${action || 'update'}`);
      
      // Small delay for cross-section updates to allow related data to process
      setTimeout(() => {
        fetchReservationsData();
      }, source && source !== 'reservations' ? 300 : 100);
    };
    
    window.addEventListener('reservationsDataUpdate', handleUpdate);
    window.addEventListener('clientsDataUpdate', handleUpdate);
    window.addEventListener('packagesDataUpdate', handleUpdate);
    window.addEventListener('financialDataUpdate', handleUpdate);
    window.addEventListener('globalDataRefresh', handleUpdate);
    
    return () => {
      window.removeEventListener('reservationsDataUpdate', handleUpdate);
      window.removeEventListener('clientsDataUpdate', handleUpdate);
      window.removeEventListener('packagesDataUpdate', handleUpdate);
      window.removeEventListener('financialDataUpdate', handleUpdate);
      window.removeEventListener('globalDataRefresh', handleUpdate);
    };
  }, [fetchReservationsData]);

  return { reservations, loading, error, stats };
};

// Clients Data Hook
export const useClientsData = (filters = {}) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false); // Chargement optimisé
  const [error, setError] = useState(null);

  const fetchClientsData = useCallback(async () => {
    try {
      setError(null);

      if (isCacheValid('clients')) {
        const cached = dataCache.clients;
        setClients(cached.data);
        setLoading(false);
        return;
      }

      if (shouldSkipFetch('clients')) {
        setTimeout(() => {
          if (isCacheValid('clients')) {
            const cached = dataCache.clients;
            setClients(cached.data);
            setLoading(false);
          } else {
            fetchClientsData();
          }
        }, 250);
        return;
      }

      setLoading(true);
      let clientsData = [];
      
      // Check if we're in standalone mode
      if (isStandaloneClient()) {
        console.log('🎯 [CLIENTS DATA] Standalone mode - loading clients from local storage');
        clientsData = getLocalData('clients');
      } else {
        console.log('🔄 [CLIENTS DATA] Development mode - loading clients from API');
        const response = await apiCall(`${config.apiUrl}/clients`);
        clientsData = response.data || response || [];
      }

      // Cache results
      dataCache.clients = {
        data: clientsData,
        timestamp: Date.now()
      };

      setClients(clientsData);
      setLoading(false);

    } catch (error) {
      console.error('Error fetching clients:', error);
      setError(error.message);
      setLoading(false);
    } finally {
      completeFetch('clients');
    }
  }, []);

  useEffect(() => {
    fetchClientsData();

    // Listen for data updates (both direct and cross-section)
    const handleUpdate = (e) => {
      const { source, action } = e?.detail || {};
      console.log(`Clients updating due to ${source || 'direct'} ${action || 'update'}`);
      
      // Small delay for cross-section updates
      setTimeout(() => {
        fetchClientsData();
      }, source && source !== 'clients' ? 300 : 100);
    };
    
    window.addEventListener('clientsDataUpdate', handleUpdate);
    window.addEventListener('reservationsDataUpdate', handleUpdate);
    window.addEventListener('financialDataUpdate', handleUpdate);
    window.addEventListener('documentsDataUpdate', handleUpdate);
    window.addEventListener('globalDataRefresh', handleUpdate);
    
    return () => {
      window.removeEventListener('clientsDataUpdate', handleUpdate);
      window.removeEventListener('reservationsDataUpdate', handleUpdate);
      window.removeEventListener('financialDataUpdate', handleUpdate);
      window.removeEventListener('documentsDataUpdate', handleUpdate);
      window.removeEventListener('globalDataRefresh', handleUpdate);
    };
  }, [fetchClientsData]);

  return { clients, loading, error };
};

// Packages Data Hook
export const usePackagesData = (filters = {}) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false); // Chargement optimisé
  const [error, setError] = useState(null);

  const fetchPackagesData = useCallback(async () => {
    try {
      setError(null);

      if (isCacheValid('packages')) {
        const cached = dataCache.packages;
        setPackages(cached.data);
        setLoading(false);
        return;
      }

      if (shouldSkipFetch('packages')) {
        setTimeout(() => {
          if (isCacheValid('packages')) {
            const cached = dataCache.packages;
            setPackages(cached.data);
            setLoading(false);
          } else {
            fetchPackagesData();
          }
        }, 250);
        return;
      }

      setLoading(true);
      let packagesData = [];
      
      // Check if we're in standalone mode
      if (isStandaloneClient()) {
        console.log('🎯 [PACKAGES DATA] Standalone mode - loading packages from local storage');
        packagesData = getLocalData('packages');
      } else {
        console.log('🔄 [PACKAGES DATA] Development mode - loading packages from API');
        const response = await apiCall(`${config.apiUrl}/forfaits`);
        packagesData = response.data || response || [];
      }

      // Cache results
      dataCache.packages = {
        data: packagesData,
        timestamp: Date.now()
      };

      setPackages(packagesData);
      setLoading(false);

    } catch (error) {
      console.error('Error fetching packages:', error);
      setError(error.message);
      setLoading(false);
    } finally {
      completeFetch('packages');
    }
  }, []);

  useEffect(() => {
    fetchPackagesData();

    // Listen for data updates with more detailed handling
    const handleUpdate = (e) => {
      const detail = (e && e.detail) ? e.detail : {};
      console.log(`Packages updating due to ${detail.action || 'update'} action`, detail);
      
      // Clear cache immediately for delete operations
      if (detail.action === 'delete') {
        delete dataCache['packages'];
      }
      
      // Always fetch fresh data
      fetchPackagesData();
    };
    
    window.addEventListener('packagesDataUpdate', handleUpdate);
    window.addEventListener('globalDataRefresh', handleUpdate);
    
    return () => {
      window.removeEventListener('packagesDataUpdate', handleUpdate);
      window.removeEventListener('globalDataRefresh', handleUpdate);
    };
  }, [fetchPackagesData]);

  return { packages, loading, error };
};

// Clear financial cache function (can be called from anywhere)
export const clearFinancialCache = () => {
  console.log('Clearing financial data cache');
  if (dataCache.financial) {
    delete dataCache.financial;
  }
};

// Financial Data Hook
export const useFinancialData = () => {
  const [financialData, setFinancialData] = useState([]);
  const [loading, setLoading] = useState(false); // Chargement optimisé
  const [error, setError] = useState(null);

  const fetchFinancialData = useCallback(async () => {
    try {
      setError(null);

      // Force refresh by skipping cache
      if (window.forceFinancialRefresh) {
        window.forceFinancialRefresh = false;
        clearFinancialCache();
      }
      
      if (isCacheValid('financial')) {
        const cached = dataCache.financial;
        setFinancialData(cached.data);
        setLoading(false);
        return;
      }

      if (shouldSkipFetch('financial')) {
        setTimeout(() => {
          if (isCacheValid('financial')) {
            const cached = dataCache.financial;
            setFinancialData(cached.data);
            setLoading(false);
          } else {
            fetchFinancialData();
          }
        }, 250);
        return;
      }

      setLoading(true);
      let transactionsData = [];
      
      const response = await apiCall(`${config.financesApiUrl}/transactions`);
      transactionsData = response.data || response || [];

      // Cache results
      dataCache.financial = {
        data: transactionsData,
        timestamp: Date.now()
      };

      setFinancialData(transactionsData);
      setLoading(false);

    } catch (error) {
      console.error('Error fetching financial data:', error);
      setError(error.message);
      setLoading(false);
    } finally {
      completeFetch('financial');
    }
  }, []);

  useEffect(() => {
    fetchFinancialData();

    // Listen for data updates (both direct and cross-section)
    const handleUpdate = (e) => {
      const detail = (e && e.detail) ? e.detail : {};
      const { source, action } = detail;
      console.log(`Financial data updating due to ${source || 'direct'} ${action || 'update'}`);
      
      // Financial data is closely tied to reservations and clients
      setTimeout(() => {
        fetchFinancialData();
      }, source && source !== 'financial' ? 300 : 100);
    };
    
    window.addEventListener('financialDataUpdate', handleUpdate);
    window.addEventListener('reservationsDataUpdate', handleUpdate);
    window.addEventListener('clientsDataUpdate', handleUpdate);
    window.addEventListener('globalDataRefresh', handleUpdate);
    
    return () => {
      window.removeEventListener('financialDataUpdate', handleUpdate);
      window.removeEventListener('reservationsDataUpdate', handleUpdate);
      window.removeEventListener('clientsDataUpdate', handleUpdate);
      window.removeEventListener('globalDataRefresh', handleUpdate);
    };
  }, [fetchFinancialData]);

  return { financialData, transactions: financialData, loading, error };
};

// Calendar Data Hook
export const useCalendarData = (filters = {}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false); // Chargement optimisé
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalEvents: 0,
    upcomingEvents: 0,
    revenueFromEvents: 0,
    eventsByType: {}
  });

  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      if (isCacheValid('calendar')) {
        const cached = dataCache.calendar;
        setEvents(cached.data);
        setStats(cached.stats);
        setLoading(false);
        return;
      }

      // API only
      const response = await apiCall(`${config.calendarApiUrl}/events`);
      const eventsData = response.data || response || [];

      // Process and filter events
      let processedEvents = eventsData;
      
      if (filters.eventType && filters.eventType !== 'all') {
        processedEvents = processedEvents.filter(event => event.type === filters.eventType);
      }
      
      if (filters.startDate && filters.endDate) {
        processedEvents = processedEvents.filter(event => {
          const eventDate = new Date(event.date);
          return eventDate >= new Date(filters.startDate) && eventDate <= new Date(filters.endDate);
        });
      }

      // Calculate statistics
      const now = new Date();
      const totalEvents = processedEvents.length;
      const upcomingEvents = processedEvents.filter(e => new Date(e.date) >= now).length;
      const revenueFromEvents = processedEvents.reduce((sum, e) => sum + (e.revenue || 0), 0);
      
      const eventsByType = processedEvents.reduce((acc, e) => {
        acc[e.type] = (acc[e.type] || 0) + 1;
        return acc;
      }, {});
      
      const statsData = {
        totalEvents,
        upcomingEvents,
        revenueFromEvents,
        eventsByType
      };

      // Cache results
      dataCache.calendar = {
        data: processedEvents,
        stats: statsData,
        timestamp: Date.now()
      };

      setEvents(processedEvents);
      setStats(statsData);
      setLoading(false);

    } catch (error) {
      console.log('Calendar data not available, using empty events');
      setError(null);
      setEvents([]);
      setStats({
        totalEvents: 0,
        upcomingEvents: 0,
        revenueFromEvents: 0,
        eventsByType: {}
      });
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCalendarData();

    // Listen for data updates
    const handleUpdate = () => fetchCalendarData();
    window.addEventListener('calendarDataUpdate', handleUpdate);
    
    return () => {
      window.removeEventListener('calendarDataUpdate', handleUpdate);
    };
  }, [fetchCalendarData]);

  return { events, loading, error, stats };
};

// Suppliers Data Hook
export const useSuppliersData = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true); // Start as loading in Electron
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchSuppliersData = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
      }
      setError(null);

      // Check cache first
      if (isCacheValid('suppliers')) {
        const cached = dataCache.suppliers;
        setSuppliers(cached.data);
        setLoading(false);
        return;
      }

      // Fetch from API (try /suppliers, then /fournisseurs)
      const urls = [`${config.apiUrl}/suppliers`, `${config.apiUrl}/fournisseurs`];
      let suppliersData = null;
      let lastError = null;

      for (const url of urls) {
        try {
          console.log(`🔄 Fetching suppliers from: ${url}`);
          const resp = await apiCall(url);
          suppliersData = resp.data || resp || [];
          console.log(`✅ Suppliers loaded: ${suppliersData.length} items`);
          break;
        } catch (e1) {
          lastError = e1;
          console.warn(`⚠️ Failed to fetch from ${url}:`, e1.message);
          
          // Try without credentials as fallback
          try {
            const resp2 = await fetch(url, { credentials: 'omit' });
            if (resp2.ok) {
              const json = await resp2.json();
              suppliersData = json.data || json || [];
              console.log(`✅ Suppliers loaded (no-creds): ${suppliersData.length} items`);
              break;
            }
          } catch (e2) {
            lastError = e2;
          }
        }
      }

      if (suppliersData == null) {
        throw lastError || new Error('Failed to fetch suppliers');
      }

      // Cache results
      dataCache.suppliers = {
        data: suppliersData,
        timestamp: Date.now()
      };

      setSuppliers(suppliersData);
      setLoading(false);
      setRetryCount(0); // Reset retry count on success

    } catch (error) {
      console.error('❌ Error fetching suppliers:', error);
      
      // Retry logic for Electron (backend might still be initializing)
      if (window.electronAPI && retryCount < 3) {
        console.log(`🔄 Retrying suppliers fetch (attempt ${retryCount + 1}/3) in 2 seconds...`);
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          fetchSuppliersData(true);
        }, 2000);
      } else {
        setError(error.message || 'Network error');
        setLoading(false);
        setSuppliers([]); // Set empty array so UI doesn't stay in loading state forever
      }
    }
  }, [retryCount]);

  useEffect(() => {
    fetchSuppliersData();

    // Listen for data updates
    const handleUpdate = () => fetchSuppliersData();
    window.addEventListener('suppliersDataUpdate', handleUpdate);
    
    return () => {
      window.removeEventListener('suppliersDataUpdate', handleUpdate);
    };
  }, [fetchSuppliersData]);

  return { suppliers, loading, error };
};

// Documents Data Hook
export const useDocumentsData = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true); // Start as loading in Electron
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    contracts: 0,
    invoices: 0,
    clientDocs: 0,
    pending: 0
  });

  const fetchDocumentsData = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
      }
      setError(null);

      // Check cache first
      if (isCacheValid('documents')) {
        const cached = dataCache.documents;
        setDocuments(cached.data);
        setStats(cached.stats);
        setLoading(false);
        return;
      }

      // Fetch from API
        console.log(`🔄 Fetching documents from: ${config.documentsApiUrl}`);
        const response = await apiCall(`${config.documentsApiUrl}`);
        let documentsData = response.data || response || [];
        
        // Map backend fields to frontend expected fields
        documentsData = documentsData.map(doc => ({
          ...doc,
          // Backend uses 'titre', frontend uses 'nom'
          nom: doc.titre || doc.nom || 'Document sans nom',
          // Extract metadata fields
          proprietaire: doc.metadata?.proprietaire || doc.proprietaire || '',
          description: doc.metadata?.description || doc.description || '',
          // Backend uses 'createdAt', frontend uses 'dateCreation'
          dateCreation: doc.metadata?.dateCreation || doc.dateCreation || doc.createdAt || new Date().toISOString().split('T')[0],
          // Keep backend fields too for compatibility
          titre: doc.titre,
          createdAt: doc.createdAt
        }));
        
        console.log(`✅ Documents loaded: ${documentsData.length} items (fields mapped from backend)`);

      // Calculate stats
      const contracts = documentsData.filter(d => d.type === 'contrat').length;
      const invoices = documentsData.filter(d => d.type === 'facture').length;
      const clientDocs = documentsData.filter(d => d.categorie === 'clients').length;
      const pending = documentsData.filter(d => d.statut === 'en_attente').length;
      
      const statsData = {
        total: documentsData.length,
        contracts,
        invoices,
        clientDocs,
        pending
      };

      // Cache results
      dataCache.documents = {
        data: documentsData,
        stats: statsData,
        timestamp: Date.now()
      };

      setDocuments(documentsData);
      setStats(statsData);
      setLoading(false);
      setRetryCount(0); // Reset retry count on success

    } catch (error) {
      console.error('❌ Error fetching documents:', error);
      
      // Retry logic for Electron (backend might still be initializing)
      if (window.electronAPI && retryCount < 3) {
        console.log(`🔄 Retrying documents fetch (attempt ${retryCount + 1}/3) in 2 seconds...`);
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          fetchDocumentsData(true);
        }, 2000);
      } else {
        setError(error.message);
        setLoading(false);
        setDocuments([]); // Set empty array so UI doesn't stay in loading state forever
        setStats({
          total: 0,
          contracts: 0,
          invoices: 0,
          clientDocs: 0,
          pending: 0
        });
      }
    }
  }, [retryCount]);

  // Upload a document (metadata + optional file)
  const uploadDocument = async (metadata, file) => {
    try {
      console.log('📤 [UPLOAD] Starting document upload...', { metadata, file });
      
      // Handle file content differently for Electron vs Browser
      let fileContent = undefined;
      
      if (file) {
        // Check if this is an Electron file (already has base64 data)
        if (file.electronFile && file.data) {
          console.log('✅ [UPLOAD] Using Electron file data (already base64)');
          // Convert base64 to data URL format
          fileContent = `data:${file.type || 'application/octet-stream'};base64,${file.data}`;
        } else if (file instanceof Blob || file instanceof File) {
          // Regular browser File/Blob - read using FileReader
          console.log('✅ [UPLOAD] Reading browser File/Blob with FileReader');
          fileContent = await new Promise((res, rej) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.onerror = () => rej(new Error('Failed to read file'));
            reader.readAsDataURL(file);
          });
        } else {
          console.warn('⚠️ [UPLOAD] Unknown file type, skipping content');
        }
      }
      
      // Map frontend fields to backend schema
      const payload = {
        // Backend uses 'titre', frontend uses 'nom'
        titre: metadata.nom || file?.name || 'Nouveau document',
        type: metadata.type,
        categorie: metadata.categorie,
        statut: metadata.statut || 'actif',
        taille: file ? file.size : 0,
        format: file ? file.type : '',
        cheminFichier: file?.path || '',
        // Store additional fields in metadata JSON
        metadata: {
          nom: metadata.nom || file?.name,
          proprietaire: metadata.proprietaire || '',
          description: metadata.description || '',
          dateCreation: metadata.dateCreation || new Date().toISOString().split('T')[0],
          contenu: fileContent
        }
      };
      
      console.log('📤 [UPLOAD] Payload prepared (mapped to backend schema):', { 
        ...payload, 
        metadata: { ...payload.metadata, contenu: fileContent ? `base64... (${fileContent.length} bytes)` : 'none' }
      });

      const result = await apiCall(`${config.documentsApiUrl}`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      invalidateCache('documents');
      window.dispatchEvent(new CustomEvent('documentsDataUpdate'));

      return result;
    } catch (error) {
      console.error('Error uploading document:', error);
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    fetchDocumentsData();

    // Listen for data updates
    const handleUpdate = () => fetchDocumentsData();
    window.addEventListener('documentsDataUpdate', handleUpdate);
    
    return () => {
      window.removeEventListener('documentsDataUpdate', handleUpdate);
    };
  }, [fetchDocumentsData]);

  return { documents, loading, error, stats, uploadDocument };
};

// CRUD Operations

// Client CRUD
export const addClient = async (clientData) => {
  try {
    // Check if we're in standalone mode
    if (isStandaloneClient()) {
      console.log('🎯 [CLIENT] Standalone mode - adding client locally');
      
      // Get existing clients from local storage
      const clients = getLocalData('clients');
      
      // Create new client with generated ID
      const newClient = {
        ...clientData,
        id: generateLocalId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Add to local data
      clients.push(newClient);
      setLocalData('clients', clients);
      
      console.log('✅ [CLIENT] Client added locally:', newClient.id);
      
      // Update cache and notify components
      invalidateCache('clients');
      notifyDataChange('client', 'create', newClient);
      
      return newClient;
    } else {
      // Development mode - use backend API
      console.log('🔄 [CLIENT] Development mode - using backend API');
      
      const result = await apiCall(`${config.apiUrl}/clients`, {
        method: 'POST',
        body: JSON.stringify(clientData)
      });
      
      const newClient = result.data || result;
      invalidateCache('clients');
      notifyDataChange('client', 'create', newClient);
      
      return newClient;
    }
  } catch (error) {
    console.error('Error adding client:', error);
    throw new Error('Erreur lors de l\'ajout du client. Assurez-vous que le serveur backend est démarré sur le port 5001.');
  }
};

export const updateClient = async (clientId, clientData) => {
  try {
    // Check if we're in standalone mode
    if (isStandaloneClient()) {
      console.log('🎯 [CLIENT] Standalone mode - updating client locally');
      
      // Get existing clients from local storage
      const clients = getLocalData('clients');
      
      // Find and update client
      const clientIndex = clients.findIndex(client => client.id === clientId);
      if (clientIndex === -1) {
        throw new Error('Client not found');
      }
      
      // Update client data
      const updatedClient = {
        ...clients[clientIndex],
        ...clientData,
        updatedAt: new Date().toISOString()
      };
      
      clients[clientIndex] = updatedClient;
      setLocalData('clients', clients);
      
      console.log('✅ [CLIENT] Client updated locally:', clientId);
      
      // Update cache and notify components
      invalidateCache('clients');
      notifyDataChange('client', 'update', { id: clientId, ...updatedClient });
      
      return updatedClient;
    } else {
      // Development mode - use backend API
      console.log('🔄 [CLIENT] Development mode - using backend API');
      
      const result = await apiCall(`${config.apiUrl}/clients/${clientId}`, {
        method: 'PUT',
        body: JSON.stringify(clientData)
      });
      
      const updatedClient = result.data || result;
      invalidateCache('clients');
      notifyDataChange('client', 'update', { id: clientId, ...updatedClient });
      
      return updatedClient;
    }
  } catch (error) {
    console.error('Error updating client:', error);
    throw new Error('Erreur lors de la mise à jour du client. Assurez-vous que le serveur backend est démarré sur le port 5001.');
  }
};

export const deleteClient = async (clientId) => {
  try {
    console.log('🗑️ Attempting to delete client:', clientId);
    console.log('🌐 API URL:', `${config.apiUrl}/clients/${clientId}`);
    
    const response = await apiCall(`${config.apiUrl}/clients/${clientId}`, {
      method: 'DELETE'
    });
    
    console.log('SUCCESS: Delete response:', response);
    
    invalidateCache('clients');
    notifyDataChange('client', 'delete', { id: clientId });
    
    return { success: true };
  } catch (error) {
    console.error('ERROR: Error deleting client:', error);
    console.error('ERROR: Error details:', {
      clientId,
      url: `${config.apiUrl}/clients/${clientId}`,
      errorMessage: error.message,
      errorStack: error.stack
    });
    throw new Error(`Erreur lors de la suppression du client ${clientId}. Backend Error: ${error.message}`);
  }
};

// Forfaits CRUD (packages)
export const addForfait = async (forfaitData) => {
  try {
    // Check if we're in standalone mode
    if (isStandaloneClient()) {
      console.log('🎯 [FORFAIT] Standalone mode - adding package locally');
      
      // Get existing packages from local storage
      const packages = getLocalData('packages');
      
      // Create new package with generated ID
      const newForfait = {
        ...forfaitData,
        id: generateLocalId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Add to local data
      packages.push(newForfait);
      setLocalData('packages', packages);
      
      console.log('✅ [FORFAIT] Package added locally:', newForfait.id);
      
      // Update cache and notify components
      invalidateCache('packages');
      notifyDataChange('package', 'create', newForfait);
      
      return newForfait;
    } else {
      // Development mode - use backend API
      console.log('🔄 [FORFAIT] Development mode - using backend API');
      
      const result = await apiCall(`${config.apiUrl}/forfaits`, {
        method: 'POST',
        body: JSON.stringify(forfaitData)
      });

      const newForfait = result.data || result;
      invalidateCache('packages');
      notifyDataChange('package', 'create', newForfait);

      return newForfait;
    }
  } catch (error) {
    console.error('Error adding forfait:', error);
    throw new Error('Erreur lors de la création du forfait. Assurez-vous que le serveur backend est démarré sur le port 5001.');
  }
};

export const updateForfait = async (forfaitId, forfaitData) => {
  try {
    const result = await apiCall(`${config.apiUrl}/forfaits/${forfaitId}`, {
      method: 'PUT',
      body: JSON.stringify(forfaitData)
    });

    const updated = result.data || result;
    invalidateCache('packages');
    notifyDataChange('package', 'update', { id: forfaitId, ...updated });

    return updated;
  } catch (error) {
    console.error('Error updating forfait:', error);
    throw new Error('Erreur lors de la mise à jour du forfait. Assurez-vous que le serveur backend est démarré sur le port 5001.');
  }
};

export const deleteForfait = async (forfaitId) => {
  try {
    // ------------ Optimistic cache update ------------
    if (dataCache.packages && Array.isArray(dataCache.packages.data)) {
      dataCache.packages.data = dataCache.packages.data.filter(pkg => Number(pkg.id) !== Number(forfaitId));
      // update timestamp so cache considered fresh briefly
      dataCache.packages.timestamp = Date.now();
      notifyDataChange('package', 'delete', { id: forfaitId });
    }
    // --------------------------------------------------

    const response = await apiCall(`${config.apiUrl}/forfaits/${forfaitId}`, {
      method: 'DELETE'
    });

    // Force immediate cache invalidation
    delete dataCache['packages'];
    globalDataVersion['packages']++;

    // Notify about the change with a slight delay to ensure UI updates
    setTimeout(() => {
      notifyDataChange('package', 'delete', { id: forfaitId });
      window.dispatchEvent(new CustomEvent('packageDataUpdate', {
        detail: { action: 'delete', data: { id: forfaitId }, timestamp: Date.now() }
      }));
    }, 100);

    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error deleting forfait:', error);
    // We already performed optimistic UI update; no rollback here.
    throw new Error('Erreur lors de la suppression du forfait. Assurez-vous que le serveur backend est démarré sur le port 5001.');
  }
};

// Calendar CRUD - API only
export const createCalendarEvent = async (eventData) => {
  const result = await apiCall(`${config.calendarApiUrl}/events`, {
    method: 'POST',
    body: JSON.stringify(eventData)
  });
  invalidateCache('calendar');
  window.dispatchEvent(new CustomEvent('calendarDataUpdate'));
  return result.data || result;
};

export const updateCalendarEvent = async (eventId, eventData) => {
  const result = await apiCall(`${config.calendarApiUrl}/events/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify(eventData)
  });
  invalidateCache('calendar');
  window.dispatchEvent(new CustomEvent('calendarDataUpdate'));
  return result.data || result;
};

export const deleteCalendarEvent = async (eventId) => {
  await apiCall(`${config.calendarApiUrl}/events/${eventId}`, {
    method: 'DELETE'
  });
  invalidateCache('calendar');
  window.dispatchEvent(new CustomEvent('calendarDataUpdate'));
  return true;
};

// Suppliers CRUD
export const addSupplier = async (supplierData) => {
  try {
    const result = await apiCall(`${config.apiUrl}/suppliers`, {
      method: 'POST',
      body: JSON.stringify(supplierData)
    });
    
    invalidateCache('suppliers');
    window.dispatchEvent(new CustomEvent('suppliersDataUpdate'));
    
    return result.data || result;
  } catch (error) {
    console.error('Error adding supplier:', error);
    throw new Error('Erreur lors de l\'ajout du fournisseur');
  }
};

export const updateSupplier = async (supplierId, supplierData) => {
  try {
    const result = await apiCall(`${config.apiUrl}/suppliers/${supplierId}`, {
      method: 'PUT',
      body: JSON.stringify(supplierData)
    });
    
    invalidateCache('suppliers');
    window.dispatchEvent(new CustomEvent('suppliersDataUpdate'));
    
    return result.data || result;
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw new Error('Erreur lors de la mise à jour du fournisseur');
  }
};

export const deleteSupplier = async (supplierId) => {
  try {
    console.log('🗑️ Attempting to delete supplier:', supplierId);
    
    const response = await apiCall(`${config.apiUrl}/suppliers/${supplierId}`, {
      method: 'DELETE'
    });
    
    console.log('SUCCESS: Delete supplier response:', response);
    
    // Force cache invalidation and UI update regardless of response
    invalidateCache('suppliers');
    window.dispatchEvent(new CustomEvent('suppliersDataUpdate'));
    
    return true;
  } catch (error) {
    console.error('ERROR: Error deleting supplier:', error);
    
    // Force cache invalidation and UI update even on error
    invalidateCache('suppliers');
    window.dispatchEvent(new CustomEvent('suppliersDataUpdate'));
    
    // Re-throw for UI error handling
    throw new Error(`Erreur lors de la suppression du fournisseur: ${error.message}`);
  }
};

// Dashboard and Reports Data
export const useDashboardData = () => {
  const { reservations, loading: reservationsLoading } = useReservationsData();
  const { clients, loading: clientsLoading } = useClientsData();
  const { packages, loading: packagesLoading } = usePackagesData();
  const { financialData, loading: financialLoading } = useFinancialData();

  const loading = reservationsLoading || clientsLoading || packagesLoading || financialLoading;

  return {
    reservations,
    clients,
    packages,
    financialData,
    loading
  };
};

export const useReservationsReportData = () => {
  const { reservations, loading, error } = useReservationsData();
  const { clients } = useClientsData();
  const { packages } = usePackagesData();

  return {
    reservations,
    clients,
    packages,
    loading,
    error
  };
};

export const useSidebarCounts = () => {
  const [counts, setCounts] = useState({
    reservations: 0,
    clients: 0,
    packages: 0,
    documents: 0,
    transactions: 0
  });
  
  const { reservations } = useReservationsData();
  const { clients } = useClientsData();
  const { packages } = usePackagesData();
  const { documents } = useDocumentsData();
  const { financialData } = useFinancialData();

  // Update counts when data changes
  useEffect(() => {
    setCounts({
      reservations: Array.isArray(reservations) ? reservations.length : 0,
      clients: Array.isArray(clients) ? clients.length : 0,
      packages: Array.isArray(packages) ? packages.length : 0,
      documents: Array.isArray(documents) ? documents.length : 0,
      transactions: Array.isArray(financialData) ? financialData.length : 0
    });
  }, [reservations, clients, packages, documents, financialData]);

  // Listen for real-time data updates
  useEffect(() => {
    const handleDataUpdate = (e) => {
      // Some events might not be CustomEvent or may have null detail
      const source = (e && e.detail && e.detail.source) ? e.detail.source : undefined;
      
      // Force refresh counts when any data changes
      setTimeout(() => {
        setCounts(prevCounts => ({
          ...prevCounts,
          reservations: Array.isArray(reservations) ? reservations.length : prevCounts.reservations,
          clients: Array.isArray(clients) ? clients.length : prevCounts.clients,
          packages: Array.isArray(packages) ? packages.length : prevCounts.packages,
          documents: Array.isArray(documents) ? documents.length : prevCounts.documents,
          transactions: Array.isArray(financialData) ? financialData.length : prevCounts.transactions
        }));
      }, 100); // Small delay to allow data to update
    };

    window.addEventListener('globalDataRefresh', handleDataUpdate);
    window.addEventListener('clientDataUpdate', handleDataUpdate);
    window.addEventListener('reservationDataUpdate', handleDataUpdate);
    window.addEventListener('packageDataUpdate', handleDataUpdate);
    window.addEventListener('documentDataUpdate', handleDataUpdate);
    window.addEventListener('financialDataUpdate', handleDataUpdate);

    return () => {
      window.removeEventListener('globalDataRefresh', handleDataUpdate);
      window.removeEventListener('clientDataUpdate', handleDataUpdate);
      window.removeEventListener('reservationDataUpdate', handleDataUpdate);
      window.removeEventListener('packageDataUpdate', handleDataUpdate);
      window.removeEventListener('documentDataUpdate', handleDataUpdate);
      window.removeEventListener('financialDataUpdate', handleDataUpdate);
    };
  }, [reservations, clients, packages, documents, financialData]);

  return counts;
};

// This function has been removed - no localStorage fallback needed

// Utility functions
export const resetAndInitializeData = async () => {
  // Clear all cache
  Object.keys(dataCache).forEach(key => {
    delete dataCache[key];
    globalDataVersion[key]++;
  });

  // No localStorage initialization needed

  // Dispatch global refresh event
  window.dispatchEvent(new CustomEvent('globalDataRefresh', {
    detail: { source: 'resetAndInitializeData' }
  }));
};

// Real-time interconnection utilities
export const syncAllSections = () => {
  console.log('🔄 Synchronizing all sections with real-time data...');
  
  // Invalidate all caches to force fresh data fetch
  Object.keys(dataCache).forEach(key => {
    invalidateCache(key);
  });
  
  // Notify all sections to refresh
  notifyDataChange('all', 'sync', { timestamp: Date.now() });
  
  console.log('SUCCESS: All sections synchronized successfully!');
};

export const getDataInterconnections = () => {
  return {
    dependencies: dataDependencies,
    version: globalDataVersion,
    cache: Object.keys(dataCache).reduce((acc, key) => {
      acc[key] = {
        cached: !!dataCache[key],
        timestamp: dataCache[key]?.timestamp || null,
        age: dataCache[key] ? Date.now() - dataCache[key].timestamp : null
      };
      return acc;
    }, {})
  };
};

export const validateDataConsistency = () => {
  console.log('🔍 Validating data consistency across sections...');
  
  const interconnections = getDataInterconnections();
  const issues = [];
  
  // Check for stale cache entries
  Object.entries(interconnections.cache).forEach(([key, info]) => {
    if (info.cached && info.age > CACHE_DURATION) {
      issues.push(`${key} cache is stale (${Math.round(info.age / 1000)}s old)`);
    }
  });
  
  if (issues.length > 0) {
    console.warn('WARNING: Data consistency issues found:', issues);
    return { valid: false, issues };
  }
  
  console.log('SUCCESS: All data is consistent across sections');
  return { valid: true, issues: [] };
};

export const fetchData = async (url, options = {}) => {
  return await apiCall(url, options);
};

export const uploadFile = async (file, type = 'document') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    const response = await fetch(`${config.apiUrl}/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error('Erreur lors du téléchargement du fichier');
  }
};

// Resource management utilities - use the same function for consistency
const mapTypeToCacheKey = getDataTypeKey;
export const createResource = async (type, data) => {
  const endpoints = {
    client: 'clients',
    reservation: 'reservations',
    package: 'forfaits',
    supplier: 'suppliers',
    document: 'documents',
    transaction: 'financial'
  };

  const endpoint = endpoints[type];
  if (!endpoint) {
    throw new Error(`Unknown resource type: ${type}`);
  }

  try {
    // Try API first
    const result = await apiCall(`${config.apiUrl}/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });

    const newResource = result.data || result;
    invalidateCache(mapTypeToCacheKey(type));
    notifyDataChange(type, 'create', newResource);

    return newResource;
  } catch (error) {
    console.error(`Error creating ${type}:`, error);
    throw new Error(`Erreur lors de la création de ${type}. Assurez-vous que le serveur backend est démarré sur le port 5001.`);
  }
};

export const updateResource = async (type, id, data) => {
  const endpoints = {
    client: 'clients',
    reservation: 'reservations',
    package: 'forfaits',
    supplier: 'suppliers',
    document: 'documents',
    transaction: 'financial'
  };

  const endpoint = endpoints[type];
  if (!endpoint) {
    throw new Error(`Unknown resource type: ${type}`);
  }

  try {
    // Try API first
    const result = await apiCall(`${config.apiUrl}/${endpoint}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });

    const updatedResource = result.data || result;
    invalidateCache(mapTypeToCacheKey(type));
    notifyDataChange(type, 'update', { id, ...updatedResource });

    return updatedResource;
  } catch (error) {
    console.error(`Error updating ${type}:`, error);
    throw new Error(`Erreur lors de la mise à jour de ${type}. Assurez-vous que le serveur backend est démarré sur le port 5001.`);
  }
};

export const deleteResource = async (type, id) => {
  const endpoints = {
    client: 'clients',
    reservation: 'reservations',
    package: 'forfaits',
    supplier: 'suppliers',
    document: 'documents',
    transaction: 'financial'
  };

  const endpoint = endpoints[type];
  if (!endpoint) {
    throw new Error(`Unknown resource type: ${type}`);
  }

  try {
    // Try API first
    await apiCall(`${config.apiUrl}/${endpoint}/${id}`, {
      method: 'DELETE'
    });

    invalidateCache(mapTypeToCacheKey(type));
    notifyDataChange(type, 'delete', { id });

    return { success: true };
  } catch (error) {
    console.error(`Error deleting ${type}:`, error);
    throw new Error(`Erreur lors de la suppression de ${type}. Assurez-vous que le serveur backend est démarré sur le port 5001.`);
  }
};

export const getResourceById = async (type, id) => {
  const endpoints = {
    client: 'clients',
    reservation: 'reservations',
    package: 'forfaits',
    supplier: 'suppliers'
  };

  const endpoint = endpoints[type];
  if (!endpoint) {
    throw new Error(`Unknown resource type: ${type}`);
  }

  return await apiCall(`${config.apiUrl}/${endpoint}/${id}`);
};
